import { forwardRef, useMemo } from 'react';
import { Select as MUISelect } from '@mui/material';
import { useOverlayScrollbars } from 'overlayscrollbars-react';
import {
  AB3, S1, S2, S3, S4, AR2, AR1,
} from '../../../theme';
import FormControl from '../../FormControl';
import InputLabel from '../../InputLabel';
import { TSelectProps } from '../index';
import { mergeSX } from '../../../services/helpers';
import { TYPOGRAPHY_BODY1, TYPOGRAPHY_BODY2 } from '../../Typography';
import {
  DEFAULT_SCROLLBAR_PARAMS,
  setCustomScrollbarInitializationAttribute,
} from '../../../services';
import FormHelperText from '../../FormHelperText';
import { CaretIcon } from '../../../icons';

interface IBuildFormControlSXParams {
  disableMinWidth: boolean;
  fullWidth: boolean;
}

const buildFormHelperTextSX = () => ({
  marginTop: 0,
  marginX: 3,
});

const buildFormControlSX = ({ disableMinWidth, fullWidth }: IBuildFormControlSXParams) => ({
  '&.MuiFormControl-root': {
    minWidth: disableMinWidth ? 'auto' : '200px',
    width: fullWidth ? '100%' : 'auto',
  },
});

const buildInputLabelSX = () => ({
  '&.MuiInputLabel-root': {
    ...TYPOGRAPHY_BODY1,
    color: S4,
    marginTop: '-3px',
    '&.MuiInputLabel-sizeSmall': {
      ...TYPOGRAPHY_BODY2,
      marginTop: '-2px',
    },
    '&.Mui-focused': {
      color: AB3,
      '&.Mui-error': {
        color: AR1,
      },
    },
  },
  '&.MuiInputLabel-shrink': {
    marginTop: 0,
  },
  '&.Mui-disabled': {
    color: S4,
  },
  '&.Mui-error': {
    color: AR1,
  },
});

const buildSelectSX = () => ({
  '& .MuiSelect-icon': {
    marginTop: '-4px',
    color: AB3,
    '&.Mui-disabled': {
      color: S3,
    },
  },
  // outlined
  '&.MuiOutlinedInput-root': {
    height: '48px',
    backgroundColor: S1,
    borderRadius: '16px',
    '&.MuiInputBase-sizeSmall': {
      height: '34px',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: `2px solid ${S3}`,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: `2px solid ${S3}`,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: `2px solid ${AB3}`,
  },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    border: `2px solid ${S3}`,
  },
  '&.Mui-error': {
    borderColor: AR1,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: AR1,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: AR2,
    },
  },
  '& .MuiSelect-select': {
    ...TYPOGRAPHY_BODY1,
    paddingLeft: '16px',
    '&.MuiInputBase-inputSizeSmall': {
      ...TYPOGRAPHY_BODY2,
    },
  },
  // filled
  '&.MuiFilledInput-root': {
    height: '48px',
    border: `2px solid ${S3}`,
    borderRadius: '16px',
    backgroundColor: 'white',
    '&.Mui-focused': {
      border: `2px solid ${S3}`,
    },

    '&:hover': {
      border: `2px solid ${AB3}`,
    },
  },
  '& .MuiSelect-filled': {
    padding: '16px 12px 16px',
    '&:focus': {
      backgroundColor: 'unset',
    },
  },
  '&.MuiInputBase-adornedStart': {
    paddingLeft: '16px',
    '& .MuiSelect-select': {
      marginLeft: '-4px',
      paddingLeft: 0,
    },
  },
});

const buildPaperSX = () => ({
  marginTop: 0.5,
  color: AB3,
  border: `2px solid ${S3}`,
  borderRadius: 4,
  boxShadow: 'none',
  boxSizing: 'border-box',
  backgroundColor: S1,
  width: 'fit-content',
  '& .MuiMenuItem-root': {
    ...TYPOGRAPHY_BODY1,
    display: 'block',
    minHeight: '56px',
    padding: '16px',
    backgroundColor: S1,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: AB3,
      color: S1,
    },
    '&:hover': {
      backgroundColor: S2,
      color: AB3,
    },
  },
  '& .MuiMenu-list': {
    padding: 0,
    maxHeight: '280px',
  },
});

const Select = forwardRef((props: TSelectProps, ref) => {
  const {
    children,
    errorMessage,
    label,
    disableMinWidth = false,
    formControlProps = {},
    paperSX,
    fullWidth = false,
    MenuProps,
    disabled = false,
    disableCustomScrollbar = false,
    sx,
    ...restProps
  } = props;

  const menuProps = useMemo(() => ({
    PaperProps: {
      sx: mergeSX(buildPaperSX(), paperSX),
    },
  }), [paperSX]);

  const [initialize, instance] = useOverlayScrollbars(DEFAULT_SCROLLBAR_PARAMS);

  const menuListProps = useMemo(() => {
    if (disableCustomScrollbar) {
      return {};
    }
    return {
      MenuListProps: {
        ref: (menuListRef: HTMLUListElement | null) => {
          if (!menuListRef) {
            instance()?.destroy();
            return;
          }
          const paperElement = menuListRef.offsetParent as HTMLElement;
          setCustomScrollbarInitializationAttribute(paperElement);
          initialize(paperElement);
        },
      },
    };
  }, [disableCustomScrollbar, initialize, instance]);

  return (
    <FormControl
      {...formControlProps}
      sx={mergeSX(buildFormControlSX({ disableMinWidth, fullWidth }), formControlProps.sx)}
    >
      {label && (
        <InputLabel
          error={!!errorMessage}
          sx={buildInputLabelSX()}
          disabled={disabled}
        >
          {label}
        </InputLabel>
      )}
      <MUISelect
        MenuProps={{
          ...menuProps,
          ...MenuProps,
          ...menuListProps,
        } as TSelectProps['MenuProps']}
        IconComponent={CaretIcon}
        label={label}
        inputRef={ref}
        disabled={disabled}
        sx={mergeSX(buildSelectSX(), sx)}
        {...restProps}
      >
        {children}
      </MUISelect>
      {!!errorMessage && (
        <FormHelperText variant="outlined" sx={buildFormHelperTextSX()} error>
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  );
});

export default Select;
